export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function formatAUD(value: number | null | undefined, options?: { showSign?: boolean }): string {
  if (value == null) return '—';
  const abs = Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const prefix = value < 0 ? '-' : (options?.showSign && value > 0 ? '+' : '');
  return `${prefix}$${abs}`;
}

export function formatFY(endingYear: number): string {
  return `FY${endingYear}`;
}

export function copyBlockText(label: string, fields: { label: string; value: string }[]): string {
  const lines = fields.map(f => `${f.label}: ${f.value}`);
  return `${label}\n${lines.join('\n')}`;
}

export function formatAustralianCalendarDate(val?: string | Date | null): string {
  if (!val) return '—';
  try {
    const str = val instanceof Date ? val.toISOString() : String(val);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [_, y, m, d] = match;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(m, 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${d} ${months[monthIndex]} ${y}`;
      }
    }
    const dObj = new Date(str);
    if (!isNaN(dObj.getTime())) {
      return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(dObj);
    }
  } catch {
    // Ignore and fallback to '—'
  }
  return '—';
}

const MIME_TO_EXT: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/bmp': '.bmp',
};

function pad(n: number, len = 2): string {
  return String(n).padStart(len, '0');
}

function buildScreenshotName(index: number, total: number, mimeType: string): string {
  const now = new Date();
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const ext = MIME_TO_EXT[mimeType] || '.png';
  if (total > 1) {
    return `Screenshot-${stamp}-${pad(index + 1)}.png`.replace('.png', ext);
  }
  return `Screenshot-${stamp}${ext}`;
}

/**
 * Extract pasted image files from clipboard DataTransfer items.
 * Only reads items where kind === 'file' and type starts with 'image/'.
 * Does not read text, HTML, URLs, or data URIs.
 * Returns normal browser File objects with safe filenames.
 */
export function extractPastedImageFiles(items: DataTransferItemList | null): File[] {
  if (!items || items.length === 0) return [];

  const imageFiles: File[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const blob = item.getAsFile();
      if (blob) {
        imageFiles.push(blob);
      }
    }
  }

  if (imageFiles.length === 0) return [];

  // Re-wrap with proper names and lastModified
  return imageFiles.map((raw, idx) => {
    const name = buildScreenshotName(idx, imageFiles.length, raw.type);
    return new File([raw], name, {
      type: raw.type || 'image/png',
      lastModified: Date.now(),
    });
  });
}